import {useEffect, useMemo, useState} from "react";

import {DocumentAmendment} from "common/flowmaster/types/amendment";
import {
  isClauseRenumberReplace,
  isRenumberTargetAutoNumbered,
} from "word/taskpane/routes/checklist/utils/apply_document_amendments";

const EMPTY: Set<DocumentAmendment> = new Set();

function getWordApi(): typeof Word | undefined {
  return typeof window !== "undefined"
    ? (window as {Word?: typeof Word}).Word
    : undefined;
}

export default function useSuppressedRenumberAmendments(
  amendments: DocumentAmendment[],
): {amendmentsReady: boolean; suppressed: Set<DocumentAmendment>} {
  const renumberAmendments = useMemo(
    () => amendments.filter(isClauseRenumberReplace),
    [amendments],
  );
  const [result, setResult] = useState<{
    renumberAmendments: DocumentAmendment[];
    suppressed: Set<DocumentAmendment>;
  } | null>(null);

  useEffect(() => {
    const wordApi = getWordApi();
    if (!wordApi || renumberAmendments.length === 0) {
      return;
    }

    let cancelled = false;
    wordApi
      .run(
        async (
          context: Word.RequestContext,
        ): Promise<Set<DocumentAmendment>> => {
          const suppressed = new Set<DocumentAmendment>();
          for (const amendment of renumberAmendments) {
            if (await isRenumberTargetAutoNumbered(context, amendment)) {
              suppressed.add(amendment);
            }
          }
          return suppressed;
        },
      )
      .then((suppressed: Set<DocumentAmendment>) => {
        if (!cancelled) {
          setResult({renumberAmendments, suppressed});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult({renumberAmendments, suppressed: new Set()});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [renumberAmendments]);

  const needsCheck = Boolean(getWordApi()) && renumberAmendments.length > 0;
  const current =
    result?.renumberAmendments === renumberAmendments ? result : null;

  return {
    amendmentsReady: !needsCheck || current !== null,
    suppressed: current?.suppressed ?? EMPTY,
  };
}
